import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title } from 'chart.js';
import { ExamContext } from '../contexts/ExamContext';
import { calculateScore } from '../utils/helpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

const Results = () => {
  const { questions, answers, resetExamState } = useContext(ExamContext);
  const [score, setScore] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    setScore(calculateScore(questions, answers));
  }, [questions, answers]);

  const totalMarksObtained = (score.correct || 0) - ((score.wrong || 0) * (1/3));
  const totalMarksRounded = Math.max(0, totalMarksObtained);
  const percentage = questions.length > 0 ? (totalMarksRounded * 100) / questions.length : 0;

  const data = {
    labels: ['Correct', 'Attempted', 'Wrong', 'Skipped'],
    datasets: [
      {
        label: 'Number of Questions',
        data: [
          score.correct || 0,
          score.totalAttempted || 0,
          score.wrong || 0,
          score.skipped || 0
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(156, 163, 175, 0.8)'
        ],
        borderColor: [
          'rgb(16, 185, 129)',
          'rgb(59, 130, 246)',
          'rgb(239, 68, 68)',
          'rgb(156, 163, 175)'
        ],
        borderWidth: 2,
        borderRadius: 8
      }
    ]
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Exam Performance Analysis',
        font: { size: 22, weight: 'bold' },
        color: '#1e40af',
        padding: { top: 10, bottom: 20 }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            return `${context.parsed.x} questions`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: questions.length,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          stepSize: Math.max(1, Math.floor(questions.length / 10)),
          font: { size: 12, weight: '600' },
          color: '#6b7280'
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          font: { size: 13, weight: '600' },
          color: '#374151'
        }
      }
    }
  };

  return (
    <div className="results-container">
      <h2>Final Results</h2>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px', gap: '12px', flexWrap: 'wrap' }}>
        <button
          className="review-questions-btn"
          onClick={() => navigate('/review')}
        >
          Review Questions
        </button>
        <button
          className="go-home-btn"
          onClick={() => {
            resetExamState();
            navigate('/');
          }}
        >
          Go to Home
        </button>
      </div>
      <div className="results-stats-grid">
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
          <h4>Total Questions</h4>
          <p style={{ color: '#374151' }}>{questions.length}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }}>
          <h4>Attempted</h4>
          <p style={{ color: '#1e40af' }}>{score.totalAttempted || 0}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' }}>
          <h4>Correct</h4>
          <p style={{ color: '#047857' }}>{score.correct || 0}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' }}>
          <h4>Wrong</h4>
          <p style={{ color: '#991b1b' }}>{score.wrong || 0}</p>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)' }}>
          <h4>Skipped</h4>
          <p style={{ color: '#4b5563' }}>{score.skipped || 0}</p>
        </div>
        <div className="stat-card" style={{
          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          gridColumn: 'span 1'
        }}>
          <h4>Total Marks</h4>
          <p style={{ color: '#047857' }}>
            {totalMarksRounded.toFixed(2)}
          </p>
        </div>
        <div className="stat-card" style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          gridColumn: 'span 1'
        }}>
          <h4>Percentage</h4>
          <p style={{ color: '#92400e' }}>
            {percentage.toFixed(2)}%
          </p>
        </div>
      </div>
      <div style={{ height: '400px', marginTop: '32px', background: 'white', padding: '20px', borderRadius: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default Results;
